import { useEffect, useState } from 'react';
import { MenuItem, Select, TextField } from '@mui/material';
import { Text } from '../../Atoms/Text';
import { DATA_TYPE_LABELS, DATA_TYPE_LIST, DataTypeValue, isDataTypeValue } from '../../../utils/customFieldTypes';

export interface DataTypeEditorProps {
  fieldType: DataTypeValue;
  choices: string[];
  onFieldTypeChange: (value: DataTypeValue) => void;
  onChoicesChange: (choices: string[]) => void;
  readOnly?: boolean;
  // The choices box answers to a different write than the type select, so a set may lock one and not the other.
  choicesReadOnly?: boolean;
  ariaLabel?: string;
}

export const parseChoices = (value: string): string[] =>
  value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

export const formatChoices = (choices: string[]): string => choices.join(', ');

export const DataTypeEditor = ({
  fieldType,
  choices,
  onFieldTypeChange,
  onChoicesChange,
  readOnly = false,
  choicesReadOnly = readOnly,
  ariaLabel = 'Data Type'
}: DataTypeEditorProps) => {
  const [choicesText, setChoicesText] = useState(() => formatChoices(choices));

  useEffect(() => {
    if (JSON.stringify(parseChoices(choicesText)) !== JSON.stringify(choices)) {
      setChoicesText(formatChoices(choices));
    }
  }, [choices, choicesText]);

  const handleChoicesTextChange = (value: string) => {
    setChoicesText(value);
    onChoicesChange(parseChoices(value));
  };

  const handleSelectChange = (value: string) => {
    if (isDataTypeValue(value)) onFieldTypeChange(value);
  };

  const isList = fieldType === DATA_TYPE_LIST;

  const choicesBlock = !isList ? null : choicesReadOnly ? (
    choices.length > 0 && (
      <Text
        color="grey6"
        size="sm"
        className="block mt-2">
        {formatChoices(choices)}
      </Text>
    )
  ) : (
    <TextField
      multiline
      minRows={3}
      size="small"
      placeholder="Enter comma-separated list values"
      value={choicesText}
      onChange={(e) => handleChoicesTextChange(e.target.value)}
      inputProps={{ 'aria-label': `${ariaLabel} choices` }}
      sx={{ width: '100%', mt: 1 }}
    />
  );

  if (readOnly) {
    return (
      <div>
        <Text
          color="grey9"
          size="sm">
          {DATA_TYPE_LABELS[fieldType]}
        </Text>
        {choicesBlock}
      </div>
    );
  }

  return (
    <div>
      <Select
        size="small"
        value={fieldType}
        onChange={(e) => handleSelectChange(e.target.value)}
        inputProps={{ 'aria-label': ariaLabel }}
        sx={{ width: '100%' }}>
        {Object.entries(DATA_TYPE_LABELS).map(([value, label]) => (
          <MenuItem
            key={value}
            value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
      {choicesBlock}
    </div>
  );
};
